<template>
    <div
        class="modal fade"
        id="bestiaryModal"
        tabindex="-1"
        aria-labelledby="bestiaryModalLabel"
        aria-hidden="true"
    >
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h5 class="modal-title" id="bestiaryModalLabel">
                        Bestiary
                    </h5>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                    ></button>
                </div>

                <div class="modal-body">
                    <div class="input-group mb-3">
                        <span class="input-group-text">Select Monster</span>
                        <select
                            v-model="selectedName"
                            @change="loadMonster"
                            class="form-select"
                        >
                            <option disabled value="">Select...</option>
                            <optgroup
                                v-for="(group, genus) in groupedBestiary"
                                :key="genus"
                                :label="genus"
                            >
                                <option
                                    v-for="m in group"
                                    :key="m.name"
                                    :value="m.name"
                                >
                                    {{ m.name }}
                                </option>
                            </optgroup>
                        </select>
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Number to Add</span>
                        <select v-model="howMany" class="form-select">
                            <option v-for="i in 10" :key="i" :value="i">
                                {{ i }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="addMonsters"
                    >
                        Add to Encounter
                    </button>

                    <!-- Hidden dismiss button for programmatic close -->
                    <button
                        type="button"
                        ref="dismissBtn"
                        class="d-none"
                        data-bs-dismiss="modal"
                    ></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRosterStore } from "@/stores/index";

export default {
    name: "BestiaryModal",
    props: {
        storyId: { type: Number, required: true },
        encounterId: { type: Number, required: true },
    },
    data() {
        return {
            store: useRosterStore(),
            selectedName: "",
            howMany: 1,
            monster: this.blankMonster(),
        };
    },
    computed: {
        groupedBestiary() {
            if (!this.store.bestiary) return {};

            const groups = {};
            this.store.bestiary.forEach((monster) => {
                const genus = monster.genus || "Other";
                if (!groups[genus]) groups[genus] = [];
                groups[genus].push(monster);
            });

            // Sort by genus and monster name
            const sortedGroups = {};
            Object.keys(groups)
                .sort((a, b) => a.localeCompare(b))
                .forEach((genus) => {
                    sortedGroups[genus] = groups[genus].sort((a, b) =>
                        a.name.localeCompare(b.name)
                    );
                });

            return sortedGroups;
        },
    },
    methods: {
        blankMonster() {
            return {
                name: "",
                genus: "",
                hp: 0,
                cr: 0,
                speed: "",
                size: "",
                lifeline: 0,
                grit: 0,
                wit: 0,
                smarts: 0,
                heart: 0,
                luck: 0,
                strange: 0,
                attacks: [],
                abilities: [],
                immunities: "",
                resistances: "",
                weaknesses: "",
                type: "",
                archetype: "",
                source: "",
            };
        },
        resetForm() {
            this.howMany = 1;
            this.selectedName = "";
            this.monster = this.blankMonster();
        },
        loadMonster() {
            const found = this.store.bestiary.find(
                (m) => m.name === this.selectedName
            );
            if (found) {
                this.monster = JSON.parse(JSON.stringify(found));
                this.monster.lifeline = 0;
            }
        },
        addMonsters() {
            if (!this.monster.name) {
                alert("Please select a monster to add.");
                return;
            }

            const story = this.store.getStoryById(this.storyId);
            if (!story) {
                alert("Story not found.");
                return;
            }

            const encounter = story.encounters.find(
                (e) => e.id === this.encounterId
            );
            if (!encounter) {
                alert("Encounter not found.");
                return;
            }

            const baseName = this.monster.name;
            let maxNum = 0;

            if (!Array.isArray(encounter.monsters)) {
                encounter.monsters = [];
            }

            encounter.monsters.forEach((m) => {
                const match = m.name.match(new RegExp(`^${baseName}\\s+(\\d+)$`));
                if (match) {
                    const num = parseInt(match[1], 10);
                    if (num > maxNum) maxNum = num;
                }
            });

            for (let i = 0; i < this.howMany; i++) {
                const copy = JSON.parse(JSON.stringify(this.monster));
                maxNum++;
                copy.name = `${baseName} ${maxNum}`;
                encounter.monsters.push(copy);
            }

            // Persist changes to store
            this.store.updateEncounter(this.storyId, encounter);

            this.resetForm();
            this.$refs.dismissBtn.click();
        },
    },
    mounted() {
        const modalEl = document.getElementById("bestiaryModal");
        modalEl?.addEventListener("hidden.bs.modal", this.resetForm);
    },
};
</script>

<style scoped>
.input-group-text {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
}
.form-select {
    background-color: rgba(255, 255, 255, 0.05);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
}
.form-select:focus {
    box-shadow: 0 0 0 0.25rem rgba(0, 191, 255, 0.25);
    border-color: #00bfff;
}
</style>

